// src/pages/HomePage.jsx
import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { BRAND } from "../config/brandConfig";
import { LIFE_DOMAINS } from "../config/lifeDomains";
import Section from "../components/Section";
import Card from "../components/Card";
import NavCallToAction from "../components/NavCallToAction";

// --- FINAL COMPONENT IMPORTS ---
import FrameworkWheel from "../components/FrameworkWheel";
import SeaStatePanel from "../components/SeaStatePanel"; // Corrected import path (assuming SeaStatePanel.jsx is directly in components)
import { STEPS } from "../config/frameworkData"; // NEW: Import the data directly
// --- END FINAL COMPONENT IMPORTS ---

// ---------- Semantic Helpers (Defined INLINE for stability) ----------
function getSemanticColor(semanticKey, tokenKey) {
  try {
    const colorKey = BRAND.semantic[semanticKey][tokenKey];
    return BRAND.colors[colorKey] || BRAND.colors.ink;
  } catch {
    return BRAND.colors.accent;
  }
}
const getBgColor = (semanticKey, tokenKey) =>
  getSemanticColor(semanticKey, tokenKey);

const subtleBg = getBgColor("surface", "subtle");
const cardBg = getBgColor("surface", "card");
const inkColor = getBgColor("text", "primary");
const ink = getBgColor("text", "primary");
const ink60 = BRAND.colors?.ink70 || BRAND.colors?.textMedium || "#667085";

// ---------- Minimal modal for Life Domains (Defined INLINE for stability) ----------
function LifeDomainModal({ activeDomain, onClose }) {
  useEffect(() => {
    if (activeDomain) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeDomain]);

  if (!activeDomain) return null;
  const d = activeDomain;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15,23,42,0.8)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        animation: "fade .2s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: BRAND.colors.card,
          padding: "2rem",
          borderRadius: BRAND.radii.lg,
          maxWidth: 800,
          width: "100%",
          boxShadow: BRAND.shadows.md,
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close dialog"
          style={{
            position: "absolute",
            right: 15,
            top: 15,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: BRAND.colors.muted,
            fontSize: 18,
          }}
        >
          ×
        </button>

        <h3
          style={{
            color: BRAND.colors.brand,
            marginTop: 0,
            marginBottom: "0.5rem",
            fontSize: 24,
          }}
        >
          {d?.title}
        </h3>
        <p
          style={{ color: BRAND.colors.muted, fontSize: 14, marginBottom: 16 }}
        >
          {d?.summary}
        </p>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}
        >
          <div>
            <h4
              style={{
                color: getBgColor("text", "primary"),
                fontSize: 16,
                fontWeight: 600,
                marginTop: 0,
              }}
            >
              What we often see
            </h4>
            <ul
              style={{
                paddingLeft: "1.2rem",
                color: BRAND.colors.muted,
                fontSize: 14,
                margin: 0,
              }}
            >
              {(d?.whatWeSee || []).map((item, i) => (
                <li key={i} style={{ marginBottom: 8 }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              style={{
                color: getBgColor("text", "primary"),
                fontSize: 16,
                fontWeight: 600,
                marginTop: 0,
              }}
            >
              How we help
            </h4>
            <ul
              style={{
                paddingLeft: "1.2rem",
                color: BRAND.colors.muted,
                fontSize: 14,
                margin: 0,
              }}
            >
              {(d?.howWeHelp || []).map((item, i) => (
                <li key={i} style={{ marginBottom: 8 }}>
                  {item}
                </li>
              ))}
            </ul>

            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: BRAND.colors.brand10,
                borderRadius: BRAND.radii.md,
              }}
            >
              <p style={{ fontSize: 13, color: BRAND.colors.ink, margin: 0 }}>
                {d?.proof}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: 24,
            padding: "0.5rem 1rem",
            background: BRAND.colors.brand,
           color: BRAND.colors.card,
            border: "none",
            borderRadius: BRAND.radii.full,
          }}
        >
          Start a Confidential Conversation
        </button>
      </div>

      <style>{`@keyframes fade { from {opacity: 0} to {opacity: 1} }`}</style>
    </div>
  );
}

// ---------- Framework Detail Panel (Defined INLINE, uses imported STEPS) ----------
function FrameworkDetailPanel({ activeStepIndex }) {
  // FIX: Access STEPS directly from import
  const step = STEPS[activeStepIndex];

  if (!step) return null;

  return (
    <aside
      aria-live="polite"
      style={{
        background: cardBg,
        border: `1px solid ${BRAND.colors.line}`,
        borderRadius: 20,
        boxShadow: BRAND.shadows.sm,
        padding: "20px 24px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 20 }}>{step.icon}</span>
        <h3 style={{ margin: 0, color: ink, fontSize: 20 }}>
          {step.id}. {step.name}
        </h3>
      </div>

      <p style={{ margin: "4px 0 12px", color: ink }}>{step.subtitle}</p>
      <div
        style={{
          height: 1,
          background: BRAND.colors.line,
          margin: "10px 0 12px",
        }}
      />

      <div style={{ marginBottom: "16px" }}>
        <h4
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: ink,
            margin: "0 0 6px 0",
          }}
        >
          In this conversation
        </h4>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.2rem",
            color: ink60,
            fontSize: 14,
          }}
        >
          {step.sessionBullets.map((b, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {b}
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          height: 1,
          background: BRAND.colors.line,
          margin: "10px 0 12px",
        }}
      />

      <div style={{ marginBottom: "16px" }}>
        <h4
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: ink,
            margin: "0 0 6px 0",
          }}
        >
          What you leave with
        </h4>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.2rem",
            color: ink60,
            fontSize: 14,
          }}
        >
          {step.outcomeBullets.map((b, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {b}
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          marginTop: 14,
          fontSize: 12,
          color: ink60,
          borderTop: `1px solid ${BRAND.colors.line}`,
          paddingTop: 12,
          fontStyle: "italic",
        }}
      >
        This process is informed by decades of institutional expertise in
        disciplined planning and behavioral finance.
      </div>
    </aside>
  );
}

export default function HomePage() {
  const [activeDomain, setActiveDomain] = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const handlePillClick = (domain) => setActiveDomain(domain);

  return (
    <div>
      {/* Modal - Use the inline definition */}
      <LifeDomainModal
        activeDomain={activeDomain}
        onClose={() => setActiveDomain(null)}
      />

      {/* --------------------- HERO --------------------- */}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)",
          gap: "2.5rem",
          alignItems: "center",
          marginBottom: "4.5rem",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: BRAND.colors.accent,
              marginBottom: "0.75rem",
            }}
          >
            Built for the pace, values, and realities of New England families
            and founders.
          </div>

          <h1
            style={{
              fontSize: 40,
              lineHeight: 1.1,
              margin: 0,
              color: BRAND.colors.textDark,
              marginBottom: "0.5rem",
            }}
          >
            Confident stewardship for complex wealth.
          </h1>

          <p
            style={{
              fontSize: 17,
              fontWeight: 500,
              color: BRAND.colors.textDark,
              maxWidth: 560,
              marginBottom: "1rem",
              letterSpacing: "0.01em",
            }}
          >
            Built on disciplined process. Guided by long-term thinking. Designed
            for New England families and founders.
          </p>

          <p
            style={{
              fontSize: 15,
              color: BRAND.colors.textMedium,
              maxWidth: 560,
              marginBottom: "1.5rem",
            }}
          >
            Wealth isn’t abstract. It sits inside marriages, businesses,
            transitions, and legacies. At Harborline, we take the long view —
            clarifying what truly matters, designing durable financial
            structures, and guiding families with a calm, disciplined hand.
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            <NavCallToAction
              to="/contact"
              label="Start a confidential conversation"
            />
            <NavLink
              to="/approach"
              style={{
                textDecoration: "none",
                fontSize: 13,
                padding: "0.45rem 1rem",
                borderRadius: BRAND.radii.full,
                border: `1px solid ${BRAND.colors.borderStrong}`,
                color: BRAND.colors.textDark,
                fontWeight: 500,
              }}
            >
              How we think about risk →
            </NavLink>
          </div>

          <p
            style={{
              fontSize: 12,
              color: BRAND.colors.textLight,
              marginTop: "0.75rem",
            }}
          >
            No noise. No guesswork. Just evidence-based decisions aligned with
            your life.
          </p>
        </div>

        <div
          style={{
            borderRadius: 20,
            padding: "1.5rem",
            background: `radial-gradient(circle at top, ${BRAND.colors.background}, ${BRAND.colors.panel})`,
            border: `1px solid ${BRAND.colors.borderStrong}`,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "0.75rem", fontSize: 16 }}>
            A Simple, Repeatable Wealth Framework
          </h3>
          <p
            style={{
              fontSize: 13,
              color: BRAND.colors.textMedium,
              margin: "0 0 0.75rem 0",
            }}
          >
            The goal is not to predict markets. The goal is to build a life that
            is funded, resilient, and intentionally designed.
          </p>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.1rem",
              fontSize: 13,
              color: BRAND.colors.textMedium,
            }}
          >
            <li>Clarify goals for family, business, and legacy</li>
            <li>Map cash flow, risk, taxes, and time horizons</li>
            <li>Design portfolios aligned to your real-life plan</li>
            <li>Integrate estate, liquidity, and tax planning</li>
            <li>Review and adjust as your life evolves</li>
          </ul>
        </div>
      </section>

      {/* --------------------- INTERACTIVE LIFE DOMAINS --------------------- */}
      <Section
        eyebrow="Integration"
        title="Your Wealth Is Connected to Your Life."
        kicker="Life doesn’t happen in categories, so our planning doesn’t either. We help you build a strategy that accounts for all the moving parts."
      >
        <div
          style={{
            borderRadius: BRAND.radii.lg,
            backgroundColor: cardBg,
            boxShadow: BRAND.shadows.md,
            border: `1px solid ${BRAND.colors.borderStrong}`,
            padding: "1.5rem 1.25rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            justifyContent: "space-between",
          }}
        >
          {LIFE_DOMAINS.map((domain) => (
            <button
              key={domain.id}
              onClick={() => handlePillClick(domain)}
              style={{
                flex: "1 1 220px",
                minWidth: 160,
                border: "1px solid transparent",
                outline: "none",
                cursor: "pointer",
                borderRadius: BRAND.radii.full,
                padding: "0.9rem 1.5rem",
                backgroundColor: subtleBg,
                color: inkColor,
                fontSize: BRAND.type.Body.size,
                fontWeight: 500,
                boxShadow: BRAND.shadows.xs,
                transition: `${BRAND.motion.base} ${BRAND.motion.ease}`,
                textAlign: "center",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = BRAND.motion.hoverLift;
                e.currentTarget.style.boxShadow = BRAND.shadows.sm;
                e.currentTarget.style.backgroundColor = cardBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = BRAND.shadows.xs;
                e.currentTarget.style.backgroundColor = subtleBg;
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = BRAND.focus.ring;
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = BRAND.shadows.xs;
              }}
            >
              {domain.label}
            </button>
          ))}
        </div>

        <div
          style={{
            marginTop: "2rem",
            padding: "1rem",
            background: BRAND.colors.brand10,
            borderRadius: BRAND.radii.md,
          }}
        >
          <h4
            style={{
              color: inkColor,
              fontSize: 16,
              fontWeight: 600,
              marginTop: 0,
            }}
          >
            Integrating the whole picture:
          </h4>
          <p
            style={{ color: BRAND.colors.textMedium, fontSize: 14, margin: 0 }}
          >
            Every complexity, from family dynamics to liquidity events, is
            mapped into your single, integrated financial architecture.
          </p>
        </div>
      </Section>

      {/* ------------------ HARBORLINE FRAMEWORK OS (Integrated Wheel + Sea-State) ------------------ */}
      <Section
        eyebrow="Our Process"
        title="The Harborline Framework"
        kicker="A structured, evidence-based way to reduce risk and bring order to complexity."
      >
        <div
          style={{
            display: "grid",
            // FIX: Use a two-column grid for the whole layout
            gridTemplateColumns: "minmax(320px, 420px) 1fr",
            gap: "2rem",
            alignItems: "start",
          }}
        >
          {/* 1. LEFT COLUMN: Wheel Component (Only renders the Wheel and Legend) */}
          <div style={{ gridColumn: "1 / 2" }}>
            <FrameworkWheel setActiveStepIndex={setActiveStepIndex} />
          </div>

          {/* 2. RIGHT COLUMN: Stacked Detail Panel and Sea-State Panel (STACKED) */}
          <div style={{ gridColumn: "2 / 3", display: "grid", gap: "1.25rem" }}>
            {/* Component 1: Framework Detail Panel (Reads data from FrameworkWheel.STEPS) */}
            <FrameworkDetailPanel activeStepIndex={activeStepIndex} />

            {/* Component 2: The Sea-State Panel (Stacked below the detail panel) */}
            <SeaStatePanel activeStepIndex={activeStepIndex} />
          </div>
        </div>
      </Section>

      {/* --------------------- UBS DISCRETION MODULE (Existing) --------------------- */}
      <Section
        eyebrow="Privacy & Trust"
        title="Built on discretion and confidentiality."
        kicker="Your privacy is paramount. We act as a trusted partner across sensitive family, business, and financial conversations."
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            padding: "1rem 1.5rem",
            backgroundColor: BRAND.colors.panelSoft,
            borderRadius: BRAND.radii.md,
            borderLeft: `4px solid ${BRAND.colors.accent}`,
            fontSize: 14,
            color: BRAND.colors.textMedium,
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>Absolute Confidentiality</p>
          <p style={{ margin: 0 }}>
            Your information, intentions, and financial structure are treated
            with absolute confidentiality. We understand the sensitivity of
            complex, multi-entity, and generational wealth discussions.
          </p>
        </div>
      </Section>

      {/* --------------------- CARD SECTION (Final Copy) --------------------- */}
      <Section
        eyebrow="What we help with"
        title="Clarity That Creates Confidence."
        kicker="From concentrated stock positions to multi-entity business structures, we help families move from scattered facts to a clear, durable strategy."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1.25rem",
          }}
        >
          <Card
            tag="For individuals"
            title="Planning around major life transitions"
            body="Liquidity events, retirement, second homes, college planning, elder care, and everything in between."
          />
          <Card
            tag="For business owners"
            title="Integrating business & personal balance sheets"
            body="Cash, risk, equity, and compensation all work in harmony, so your company and personal finances reinforce each other."
          />
          <Card
            tag="For families"
            title="Generational & legacy conversations"
            body="From trusts and education to conversations that strengthen communication across generations."
          />
        </div>
      </Section>

      {/* --------------------- WHO WE WORK BEST WITH --------------------- */}
      <Section
        eyebrow="Who we work best with"
        title="For families and founders who want a long-term thinking partner."
        kicker="We’re a good fit when you want calm, disciplined guidance—not product pitches or market calls."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1.25rem",
          }}
        >
          <Card
            tag="Founders & entrepreneurs"
            title="Most of your wealth lives in your company."
            body="You’re navigating growth, liquidity, or transition—and you want a trusted partner to turn business success into durable personal wealth."
          />
          <Card
            tag="Executives & professionals"
            title="Your compensation is complex."
            body="Equity, RSUs, bonuses, and pensions all need to work together. We help you turn a demanding career into a stable, flexible financial life."
          />
          <Card
            tag="Multi-generational families"
            title="You’re thinking in decades, not quarters."
            body="You want clear structures, thoughtful gifting, and healthy conversations about money across generations—without drama or confusion."
          />
        </div>
      </Section>

      {/* --------------------- BY THE NUMBERS --------------------- */}
      <Section
        eyebrow="By the numbers"
        title="A practice built for long-term stewardship."
        kicker="A few simple markers of the type of work we do and the clients we tend to serve."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.5rem",
            padding: "1.5rem",
            backgroundColor: BRAND.colors.card,
            borderRadius: BRAND.radii.lg,
            boxShadow: BRAND.shadows.md,
            border: `1px solid ${BRAND.colors.border}`,
          }}
        >
          {[
            { label: "Years of advisory experience", value: "20+" },
            { label: "Families & businesses advised", value: "100+" },
            { label: "Typical client investable assets", value: "$1M–$25M" },
            { label: "Primary focus", value: "New England HNW" },
          ].map((item) => (
            <div key={item.label}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: BRAND.colors.accent,
                  marginBottom: 4,
                }}
              >
                {item.value}
              </div>
              <div style={{ fontSize: 13, color: BRAND.colors.textMedium }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* --------------------- NEXT STEP --------------------- */}
      <Section
        eyebrow="Next step"
        title="If this sounds like the kind of relationship you’ve been looking for, let’s talk."
        kicker="A brief, confidential conversation is often enough to see whether we’re a good fit for each other."
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1.75rem",
            alignItems: "center",
            padding: "1.5rem 1.75rem",
            borderRadius: BRAND.radii.lg,
            background:
              "linear-gradient(135deg, rgba(11,60,93,0.06), rgba(15,23,42,0.02))",
            border: `1px solid ${BRAND.colors.borderStrong}`,
          }}
        >
          <div style={{ flex: "1 1 260px" }}>
            <p
              style={{
                fontSize: 15,
                color: BRAND.colors.textMedium,
                margin: 0,
                maxWidth: 460,
              }}
            >
              We’ll use the first meeting to understand where you are today,
              what decisions are on your mind, and how you’d like your wealth to
              support the next chapter of your life.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              alignItems: "center",
            }}
          >
            <NavCallToAction to="/contact" label="Book an intro call" />
            <NavLink
              to="/about"
              style={{
                textDecoration: "none",
                fontSize: 13,
                padding: "0.45rem 1rem",
                borderRadius: BRAND.radii.full,
                border: `1px solid ${BRAND.colors.borderStrong}`,
                color: BRAND.colors.textDark,
                fontWeight: 500,
              }}
            >
              Learn more about the firm →
            </NavLink>
          </div>
        </div>
      </Section>

      {/* --------------------- FOOTER LINE --------------------- */}
      <p
        style={{
          textAlign: "center",
          fontSize: 16,
          fontWeight: 500,
          color: BRAND.colors.accent,
          opacity: 0.8,
          marginTop: "3rem",
          padding: "1.5rem",
          borderTop: `1px solid ${BRAND.colors.border}`,
        }}
      >
        We don’t manage markets. We manage risk, behavior, and decisions.
      </p>

      {/* spacer to guarantee scroll on huge monitors */}
      <div style={{ height: "18vh" }} />
    </div>
  );
}
